import { Link } from "react-router-dom";
import { ShieldAlert, FileText, ChevronRight } from "lucide-react";

/**
 * Compact embeddable Terms + Disclaimer block.
 * - Used at the bottom of the public landing page.
 * - Used above the library on the authenticated /account page.
 * - Always includes a link to the full /legal page.
 */
export default function TermsBlock({ id = "default", compact = false }) {
  return (
    <section
      data-testid={`terms-block-${id}`}
      className="border border-amber-500/30 bg-amber-500/[0.04]"
    >
      <header className="px-4 py-2 border-b border-amber-500/20 flex items-center gap-2 flex-wrap">
        <ShieldAlert size={12} className="text-amber-400" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
          DISCLAIMER · TERMS_OF_SERVICE · v1.0
        </span>
        <Link
          to="/legal"
          data-testid={`terms-block-${id}-legal-link`}
          className="ml-auto text-[10px] font-mono uppercase tracking-wider text-amber-300 hover:text-white inline-flex items-center gap-1"
        >
          READ_FULL_TERMS <ChevronRight size={10} />
        </Link>
      </header>

      <div className="p-4 space-y-4 text-[12.5px] text-zinc-300 leading-relaxed">
        {/* DISCLAIMER */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300 mb-1.5 inline-flex items-center gap-1">
            <ShieldAlert size={10} /> DISCLAIMER
          </div>
          <p className="text-zinc-300">
            <span className="font-mono text-white">THE_MINDSTEIN</span> uses
            AI-generated replies and automated source ranking for{" "}
            <span className="text-white">informational, research, and entertainment purposes only</span>.
            The content on this site is intended to be helpful and fun, but it
            is <span className="text-white">not legal, medical, financial, or professional advice</span>.
            Users should independently verify any important information before
            relying on it.
          </p>
        </div>

        {!compact && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300 mb-1.5 inline-flex items-center gap-1">
              <FileText size={10} /> TERMS OF SERVICE
            </div>
            <p className="text-zinc-400 mb-2">
              By using THE_MINDSTEIN, you agree to these Terms.
            </p>
            <ol
              data-testid={`terms-block-${id}-list`}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 list-decimal list-inside marker:text-amber-400 marker:font-mono text-zinc-400"
            >
              <li>
                <span className="text-white">Purpose</span> — AI-assisted research and idea platform for informational and entertainment purposes only. Not legal, medical, financial, or professional advice.
              </li>
              <li>
                <span className="text-white">Accounts and access</span> — sign in to use protected features. Access may be granted through a paid subscription or active free trial. Keep your login details secure.
              </li>
              <li>
                <span className="text-white">Acceptable use</span> — don&apos;t misuse the service, bypass access controls, scrape data without permission, or use the platform for unlawful purposes.
              </li>
              <li>
                <span className="text-red-300">Prohibited use — arms, military, ITAR</span> —
                MindStein <b>strictly prohibits</b> any use related to arms,
                weapons, ammunition, ordnance, military equipment, defense
                aerospace systems, or any activity violating US export
                controls (ITAR / EAR / USML) or Canadian export-control laws.
                Users attempting these uses will be{" "}
                <b>permanently banned, held personally liable</b>, and their
                identifying information (email, IP, device fingerprints,
                activity logs) will be disclosed to{" "}
                <b>law-enforcement agencies in any jurisdiction</b> under
                lawful warrant, subpoena, or investigative request without
                additional notice to the user.
              </li>
              <li>
                <span className="text-white">AI content</span> — the platform may generate automated replies, summaries, rankings, and suggestions. We do not guarantee completeness, accuracy, or fitness for any specific purpose. Verify important information independently.
              </li>
              <li>
                <span className="text-white">Subscriptions and trials</span> — trial and subscription access are controlled by your account status and the plan you select. Features, pricing, or access rules may change over time.
              </li>
              <li>
                <span className="text-emerald-300">30-day subscription honour</span> —
                once you complete the <b>first bot use</b> after subscribing,
                a 30-day billing cycle is activated and honoured to prevent
                misuse of the platform. You may cancel to prevent renewal at
                any point <b>before</b> the next 30-day cycle starts. Refunds
                are not issued inside an active monthly cycle once the bot
                has been used.
              </li>
              <li>
                <span className="text-emerald-300">Annual plans (Elite Annual · Pro Annual)</span> —
                the same first-bot-use rule applies; annual plans carry a{" "}
                <b>30-day refund window</b> from that first use. Inside the
                window, refunds are processed as: one month charged at the
                equivalent monthly-tier price ($29.99 or $49.99), the remainder
                of the annual fee refunded. Outside the window the annual
                subscription is honoured to term with cancel-anytime-before-
                renewal.
              </li>
              <li>
                <span className="text-white">Intellectual property</span> — THE_MINDSTEIN and its software belong to us or our licensors. You may not copy, resell, or reverse engineer the service. Content you upload remains yours, but you give us permission to process it to provide the service.
              </li>
              <li>
                <span className="text-white">Liability</span> — the service is provided &quot;as is&quot; and &quot;as available&quot;. To the fullest extent allowed by law, we are not responsible for losses caused by use of the platform, AI outputs, service downtime, or third-party source errors.
              </li>
              <li>
                <span className="text-white">Changes</span> — we may update these Terms from time to time. Continued use of the service means you accept the updated Terms.
              </li>
              <li>
                <span className="text-white">Contact</span> — for questions about these Terms, contact the site owner or operator.
              </li>
              <li>
                <span className="text-emerald-300">Flexibility Subscription Terms &amp; Rebate Policy</span> —
                <b> Activation Rebate:</b> upon formal commitment to any paid
                subscription tier, the account is automatically credited a
                <b> 50% rebate</b> against the first month&apos;s standard
                rate (valid for up to <b>three bot deployments</b> per account).{" "}
                <b>30-Day Cancellation &amp; Grace Period:</b> annual
                subscriptions may be cancelled within 30 days of the first
                bot use; the account is charged one month at the equivalent
                monthly-tier rate and the remainder is fully refunded.{" "}
                <b>First-Renewal Bonus:</b> annual plans (Elite Annual, Pro
                Annual) and Team plans receive an automatic{" "}
                <b>+100 free MindStein usages</b> credit on the first successful
                renewal (one-time offer per account).{" "}
                <b>$0.01 Verification:</b> a one-cent authorisation is placed
                on the payment method to verify the card belongs to a real
                person before the 7-day free trial is activated; the
                authorisation is <b>auto-refunded</b> on the same request. All
                rebate and refund settlements are processed through Stripe;
                please allow 5-10 business days for funds to appear.
              </li>
              <li>
                <span className="text-pink-300">Team Plan Policy</span> —
                Team plans (Team Monthly · $149 / mo · 5 seats;{" "}
                Team Unlimited · $249 / mo · unlimited seats) provide a
                <b> single shared pool of 150 MindStein usages / 30 days</b> plus a
                one-time <b>+100 welcome bonus</b> on commit. On the{" "}
                <b>second successful monthly renewal</b>, the account is
                credited a <b>+50 uses bonus per 5 verified seats</b>{" "}
                (one-time). On the <b>third successful monthly renewal</b>,
                team plans also receive a flat{" "}
                <b>+100 free MindStein usages</b> credit (one-time, both team plans).
                Extra seats on Team Monthly are{" "}
                <b>+$49 / seat / month</b>. The <b>3-deployment
                Activation Rebate</b> does not apply to team plans — team
                economics are already reflected in the base price. If a
                monthly commitment is not desired, a <b>flat pay-once
                $100 → 150 MindStein usages</b> pack is available at any time via
                the top-up shelf; no subscription is created and the uses
                never expire.
              </li>
            </ol>
          </div>
        )}
      </div>
    </section>
  );
}
